import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { catchError, combineLatest, filter, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { LenderUser, Team } from 'ng2-app/models/dto/lender-user.model';
import { GetFundingDeskUsers } from 'ng2-app/store/funnel/funding-desk-funnel.action';
import { FundingDeskFunnelState } from 'ng2-app/store/funnel/funding-desk-funnel.state';
import {
  SetSelectedTabByName,
  SetTodayTabs,
  SetUserActionItemsUser,
  SubscribeToCompletedUserActionItems,
  UnsetActiveUserActionItem,
  UnsubscribeFromCompletedUserActionItems
} from 'ng2-app/modules/today/state/user-action-items.action';
import { GetTodayBadgeCounts } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.action';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { RequestCounts } from 'ng2-app/modules/today/services/today-badge-counts.service';
import { TeamActions } from 'app/store/team/team.action';
import { TeamState } from 'ng2-app/store/team/team.state';

@Component({
  selector: 'app-today',
  templateUrl: './today.component.html',
  styleUrls: ['./today.component.scss'],
  standalone: false
})
export class TodayComponent implements OnInit, OnDestroy {

  @Select(UserActionItemsState.total) count$: Observable<number>;
  @Select(UserActionItemsState.loading) loading$: Observable<boolean>;
  @Select(UserActionItemsState.userId) userId$: Observable<number>;
  @Select(UserActionItemsState.selectedTabIndex) selectedTabIndex$: Observable<number>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(LenderUserState.hasPermissions([
    'canSeeTodayCallHistoryTab',
    'canViewTodayMessagingTabs',
    'canViewTodayUserDropDown',
    'canViewNewTodayPageTodayTab', // I named this annoying on purpose. To match.
  ])) permissions$: Observable<any>;
  @Select(LenderUserState.hasPermission('viewMultipleTeams')) viewMultipleTeams$: Observable<boolean>;
  @Select(TodayBadgeCountsState.missedCalls) missedCallsCount$: Observable<number>;
  @Select(TodayBadgeCountsState.unreadEmailThreads) unreadEmailThreadCount$: Observable<number>;
  @Select(TodayBadgeCountsState.unreadSmsThreads) unreadSmsThreadCount$: Observable<number>;
  @Select(TodayBadgeCountsState.today) todayBadgeCount$: Observable<number>;
  @Select(TodayBadgeCountsState.requests) requestsBadgeCounts$: Observable<RequestCounts>;
  @Select(TodayBadgeCountsState.followUp) followUpBadgeCount$: Observable<number>;

  teams$: Observable<Team[]> = this.store.select( TeamState.teams );
  teamMembers$: Observable<any>;
  lenderUser: LenderUser;

  destroyed$ = new Subject<boolean>();
  authUserIsFundingDesk: boolean;

  conditionalTabPermissions = {
    today: 'canViewNewTodayPageTodayTab',
    email: 'canViewTodayMessagingTabs',
    sms: 'canViewTodayMessagingTabs',
  };

  activeTabs: Array<string> = [
    'today',
    'requests',
    'email',
    'sms',
    'calls',
    'completed',
  ];

  constructor(
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.lenderUser$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe( lenderUser => {
      this.lenderUser = lenderUser;
      this.store.dispatch(new SubscribeToCompletedUserActionItems(lenderUser));
      this.store.dispatch( new GetTodayBadgeCounts(lenderUser.id) );
      this.store.dispatch( new SetUserActionItemsUser(lenderUser) );
      this.store.dispatch(new TeamActions.GetTeams());
      if( lenderUser.roles.includes('fundingDesk') ) {
        this.authUserIsFundingDesk = true;
        this.store.dispatch(new GetFundingDeskUsers());
        this.teamMembers$ = this.store.select( FundingDeskFunnelState.fundingDeskUsers );
      } else {
        this.store.select(LenderUserState.teamMembers).subscribe(team => {
         this.teamMembers$ = of(team?.sort((a, b) => a.fullName < b.fullName ? -1 : 1));
        });
      }
    });

    this.viewMultipleTeams$.pipe(
      takeUntil(this.destroyed$),
      catchError(() => of()),
    ).subscribe(permitted => {
      // if the user is permitted to view multiple teams, let's display the configured admin teams
      if (permitted) {
        combineLatest([this.store.select(LenderUserState.teamMembers), this.teams$])
        .subscribe(([currentTeam, adminTeams]) => {
              const teams = [...adminTeams];
              let lenderUsers: LenderUser | any = [];
              if (teams.length > 0) {
                const self: LenderUser | any = currentTeam?.find(u => u.id === this.lenderUser.id);
                teams.forEach((team) => {
                  const users = team?.lender_users?.map(lu => lu);
                  lenderUsers = lenderUsers?.concat(users);
                });
                // Add the current logged in user to the dropdown list, if missing
                const selfUserInList = lenderUsers?.some(l => l.id === self.id);
                if (!selfUserInList) {
                  lenderUsers = lenderUsers?.concat(self);
                }

                if (lenderUsers.length > 1) {
                  this.teamMembers$ = of(lenderUsers.sort((a, b) => a.fullName < b.fullName ? -1 : 1));
                }
              }
          });
      }
    });

    this.permissions$.pipe(
      takeUntil(this.destroyed$),
    ).subscribe( permissions => {
      const previous = this.activeTabs;
      this.activeTabs = this.activeTabs.filter( tab => {
        const tabPermission = this.conditionalTabPermissions[tab];
        return !tabPermission || permissions[tabPermission];
      });

      if(this.activeTabs?.length !== previous?.length) {
        this.store.dispatch( new SetTodayTabs( this.activeTabs ));
      }

    });
    this.store.dispatch( new SetTodayTabs( this.activeTabs ));
  }

  onUserIdChanged({ value }): void {
    this.teamMembers$.pipe(
      take(1),
      filter( users => users.length ),
      map( users => users?.filter( user => user.id === value)[0] )
    ).subscribe( user => {
      this.store.dispatch( new SetUserActionItemsUser(user) );
    });
    // re-fetch today badge counts with new user ID
    this.store.dispatch( new GetTodayBadgeCounts(value) );

  }

  selectedTabChange($event): void {
    this.store.dispatch( new SetSelectedTabByName(this.activeTabs[$event.index]) );
  }

  ngOnDestroy(): void {
    this.store.dispatch( new UnsetActiveUserActionItem() );
    this.store.dispatch(new UnsubscribeFromCompletedUserActionItems(this.lenderUser));
  }

}
